/// <reference types="vite/client" />

import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import React, { type FormEvent } from 'react'
import { AuthShell } from './AuthShell'
import { apiPost } from '../../lib/api'
import { AUTH_RESET_PASSWORD } from '../../lib/endpoints'
import { colors } from '../../config/themeAuth'

type ApiResponse = {
  status: number
  message: string
}

type StrengthLevel = { label: string; width: string; color: string }

function getStrength(pw: string): StrengthLevel {
  if (!pw) return { label: 'Enter a password', width: '0%', color: 'transparent' }
  if (pw.length < 5) return { label: 'Too short', width: '25%', color: '#e55' }
  if (pw.length < 8) return { label: 'Could be stronger', width: '50%', color: '#f4a04a' }
  if (pw.length < 12) return { label: 'Good password', width: '75%', color: '#8bc34a' }
  return { label: 'Strong password ✓', width: '100%', color: 'var(--accent)' }
}

export default function ResetPassword() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirm] = React.useState('')
  const [showPassword, setShowPassword] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState(false)

  const strength = getStrength(password)
  const passwordsMatch = password.length > 0 && password === confirmPassword
  const canSubmit = password.length >= 8 && passwordsMatch && !!token

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setError(null)
    setLoading(true)
    try {
      await apiPost<ApiResponse, { token: string; password: string; confirmPassword: string }>(
        AUTH_RESET_PASSWORD,
        { token, password, confirmPassword }
      )
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password reset failed')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This password reset link is missing or has already been used.">
        <Link
          to="/forgot-password"
          className="block w-full rounded-2xl px-4 py-3 text-center text-sm font-semibold transition-all"
          style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
        >
          Request a new link
        </Link>
      </AuthShell>
    )
  }

  if (success) {
    return (
      <AuthShell title="Password updated!" subtitle="Your password has been changed. You can now sign in with your new credentials.">
        <button
          type="button"
          onClick={() => navigate('/signin', { replace: true })}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all"
          style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
        >
          Sign in now
        </button>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Set new password"
      subtitle="Your reset link is valid. Choose a strong password to secure your account."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            New password
          </label>
          <div className="relative mt-2">
            <input
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="nudge-input w-full rounded-2xl border px-4 py-3 pr-16 text-sm"
              style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="At least 8 characters"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(s => !s)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold"
              style={{ color: colors.muted }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>

          {password.length > 0 && (
            <div className="mt-2 space-y-1.5">
              <div className="h-[3px] w-full overflow-hidden rounded-full" style={{ backgroundColor: 'var(--border2)' }}>
                <div className="h-full rounded-full transition-all duration-300" style={{ width: strength.width, backgroundColor: strength.color }} />
              </div>
              <p className="text-[11px]" style={{ color: strength.color }}>
                {strength.label}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Confirm password
          </label>
          <input
            value={confirmPassword}
            onChange={e => setConfirm(e.target.value)}
            className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
            style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
            type="password"
            autoComplete="new-password"
            placeholder="Repeat your password"
            required
          />
          {confirmPassword.length > 0 && !passwordsMatch && (
            <p className="mt-1.5 text-xs" style={{ color: colors.darkOrange }}>
              Passwords don't match.
            </p>
          )}
        </div>

        {error ? (
          <div className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: colors.border, color: colors.darkOrange, backgroundColor: colors.sand }}>
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-60"
          style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
        >
          {loading ? 'Updating…' : 'Update password'}
        </button>
      </form>

      <p className="mt-6 text-sm" style={{ color: colors.muted }}>
        Remembered it?{' '}
        <Link to="/signin" className="font-semibold underline" style={{ color: colors.ink }}>
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  )
}
